import type { DeforestationLayerStates } from "../types/deforestation";

export const PMTILES_SOURCES = {
  cadastre: "/demos/static/pmtiles/nab-deforestation/cadastre.pmtiles",
  property: "/demos/static/pmtiles/nab-deforestation/property.pmtiles",
  capad: "/demos/static/pmtiles/nab-deforestation/capad-terrestrial.pmtiles",
  ramsar: "/demos/static/pmtiles/nab-deforestation/ramsar-wetlands.pmtiles",
  worldHeritage:
    "/demos/static/pmtiles/nab-deforestation/world-heritage-areas.pmtiles",
  nationalHeritage:
    "/demos/static/pmtiles/nab-deforestation/national-heritage-list.pmtiles",
  commonwealthHeritage:
    "/demos/static/pmtiles/nab-deforestation/commonwealth-heritage-list.pmtiles",
  regionalForestAgreements:
    "/demos/static/pmtiles/nab-deforestation/regional-forest-agreements.pmtiles",
  qldVegManagement:
    "/demos/static/pmtiles/nab-deforestation/qld-veg-management.pmtiles",
  // PoC-2: cleaned, AOI-scoped South-Burnett bushfire boundaries replace
  // the 1.2 GB national GA layer.
  bushfireHistory:
    "/demos/static/pmtiles/nab-deforestation/bushfire-clean.pmtiles",
  koalaHabitat: "/demos/static/pmtiles/nab-deforestation/koala-habitat.pmtiles",
  criticalHabitat:
    "/demos/static/pmtiles/nab-deforestation/critical-habitat.pmtiles",
};

export const NVIS_RASTER_TILES =
  "/demos/static/raster/nab-deforestation/nvis-l2/{z}/{x}/{y}.webp";
export const NVIS_RASTER_MIN_ZOOM = 3;
export const NVIS_RASTER_MAX_ZOOM = 13;

export type ImageryWindow = "2020" | "2024" | "2025" | "latest";

export const IMAGERY_RASTER_TILES: Record<ImageryWindow, string> = {
  "2020":
    "/demos/static/raster/nab-deforestation/imagery-hires-2020/{z}/{x}/{y}.webp",
  "2024":
    "/demos/static/raster/nab-deforestation/imagery-hires-2024/{z}/{x}/{y}.webp",
  "2025":
    "/demos/static/raster/nab-deforestation/imagery-hires-2025/{z}/{x}/{y}.webp",
  latest:
    "/demos/static/raster/nab-deforestation/imagery-hires-latest/{z}/{x}/{y}.webp",
};
export const IMAGERY_RASTER_MIN_ZOOM = 10;
export const IMAGERY_RASTER_MAX_ZOOM = 14;

export const LULC_RASTER_TILES: Record<"2025", string> = {
  "2025": "/demos/static/raster/nab-deforestation/lulc-2025/{z}/{x}/{y}.png",
};
export const LULC_RASTER_MIN_ZOOM = 10;
export const LULC_RASTER_MAX_ZOOM = 15;

// AU Forest 1 m binary extent mask (forest / non-forest only) —
// from tile-au-forest-raster.py.
export const FOREST_RASTER_TILES: Record<"2020" | "2025", string> = {
  "2020":
    "/demos/static/raster/nab-deforestation/au-forest-2020/{z}/{x}/{y}.png",
  "2025":
    "/demos/static/raster/nab-deforestation/au-forest-2025/{z}/{x}/{y}.png",
};
export const FOREST_RASTER_MIN_ZOOM = 10;
export const FOREST_RASTER_MAX_ZOOM = 17;

// SatSure Crown Cover 5-class paletted raster (Unknown / Woodland /
// Open / Closed Forest) — from tile-forest-raster.py.
export const CROWN_COVER_RASTER_TILES: Record<"2020" | "2025", string> = {
  "2020": "/demos/static/raster/nab-deforestation/forest-2020/{z}/{x}/{y}.png",
  "2025": "/demos/static/raster/nab-deforestation/forest-2025/{z}/{x}/{y}.png",
};
export const CROWN_COVER_RASTER_MIN_ZOOM = 10;
export const CROWN_COVER_RASTER_MAX_ZOOM = 17;

export const FOREST_CHANGE_RASTER_TILES =
  "/demos/static/raster/nab-deforestation/forest-change/{z}/{x}/{y}.png";
export const FOREST_CHANGE_RASTER_MIN_ZOOM = 10;
export const FOREST_CHANGE_RASTER_MAX_ZOOM = 17;

export const DEFORESTATION_RISK_RASTER_TILES =
  "/demos/static/raster/nab-deforestation/deforestation-risk-2025/{z}/{x}/{y}.png";
export const DEFORESTATION_RISK_RASTER_MIN_ZOOM = 6;
export const DEFORESTATION_RISK_RASTER_MAX_ZOOM = 14;

export const PORTFOLIO_PIDS_URL =
  "/demos/static/pmtiles/nab-deforestation/nab-portfolio-pids.json";

export const PARCEL_STATS_URL =
  "/demos/static/pmtiles/nab-deforestation/parcel-stats.json";

// Full-dataset parcel index (every parcel + a representative coordinate)
// so the search box can match parcels outside the current viewport.
export const PARCEL_SEARCH_INDEX_URL =
  "/demos/static/pmtiles/nab-deforestation/parcel-search-index.json";

export const PMTILES_SOURCE_LAYERS = {
  cadastre: "cadastre",
  property: "property",
  capad: "capad_terrestrial",
  ramsar: "ramsar_wetlands",
  worldHeritage: "world_heritage_areas",
  nationalHeritage: "national_heritage_list",
  commonwealthHeritage: "commonwealth_heritage_list",
  regionalForestAgreements: "regional_forest_agreements",
  qldVegManagement: "qld_veg_management",
  bushfireHistory: "bushfire_clean",
  koalaHabitat: "koala_habitat",
  criticalHabitat: "critical_habitat",
};

export const DEFAULT_LAYER_STATES: DeforestationLayerStates = {
  imagery2020: { visible: false, opacity: 1, color: "#000000" },
  imagery2024: { visible: false, opacity: 1, color: "#000000" },
  imagery2025: { visible: false, opacity: 1, color: "#000000" },
  imageryLatest: { visible: false, opacity: 1, color: "#000000" },
  forest2020: { visible: false, opacity: 1, color: "#000000" },
  forest2025: { visible: false, opacity: 1, color: "#000000" },
  crownCover2020: { visible: false, opacity: 1, color: "#000000" },
  crownCover2025: { visible: false, opacity: 1, color: "#000000" },
  forestChange: { visible: false, opacity: 1, color: "#000000" },
  deforestationRisk: { visible: false, opacity: 1, color: "#000000" },
  lulc2025: { visible: false, opacity: 0.7, color: "#000000" },
  cadastre: { visible: true, opacity: 0.8, color: "#7c3aed" },
  property: { visible: false, opacity: 0.8, color: "#0ea5e9" },
  portfolio: { visible: false, opacity: 0.9, color: "#f97316" },
  protectedMatters: { visible: false, opacity: 1, color: "#16a34a" },
  ramsarWetlands: { visible: false, opacity: 1, color: "#06b6d4" },
  worldHeritage: { visible: false, opacity: 1, color: "#a855f7" },
  nationalHeritage: { visible: false, opacity: 1, color: "#ec4899" },
  commonwealthHeritage: { visible: false, opacity: 1, color: "#f59e0b" },
  listedCriticalHabitat: { visible: false, opacity: 1, color: "#ef4444" },
  regionalForestAgreements: { visible: false, opacity: 1, color: "#0d9488" },
  regulatedVeg: { visible: false, opacity: 1, color: "#84cc16" },
  bushfireHistory: { visible: false, opacity: 1, color: "#dc2626" },
  koalaHabitat: { visible: false, opacity: 1, color: "#14b8a6" },
  nvisVegetation: { visible: false, opacity: 1, color: "#22c55e" },
};

export const AOI_CAMERA = {
  center: [151.6, -26.1] as [number, number],
  zoom: 11,
};

export interface LayerInfo {
  key: keyof DeforestationLayerStates;
  label: string;
  description: string;
  attribution: string;
  available: boolean;
  // Month/window the underlying snapshot was captured, shown in the layer
  // info popover. Only set for layers whose data represents a point-in-time
  // capture (e.g. the SatSure forest composites).
  snapshot?: string;
  assessmentPeriod?: string;
}

export interface LayerGroupConfig {
  group: string;
  layers: LayerInfo[];
}

export const LAYER_DISPLAY_CONFIG: LayerGroupConfig[] = [
  {
    group: "Boundaries",
    layers: [
      {
        key: "cadastre",
        label: "Cadastre",
        description:
          "Cadastral parcel boundaries showing individual land parcels, coloured by forest change percentage.",
        attribution: "Geoscape Australia — G-NAF & Cadastre",
        available: true,
      },
      {
        key: "property",
        label: "Property",
        description:
          "Property boundaries aggregating one or more cadastral parcels into a single landholding.",
        attribution: "Geoscape Australia — Land Parcels (Property)",
        available: true,
      },
    ],
  },
  {
    group: "Portfolio",
    layers: [
      {
        key: "portfolio",
        label: "Demo Portfolio",
        description:
          "Demo portfolio of 201 cadastral parcels across 49 illustrative clusters, highlighted as an outline on top of the cadastre layer.",
        attribution: "Geoscape demo dataset — derived from Geoscape Cadastre",
        available: true,
      },
    ],
  },
  {
    group: "Context",
    layers: [
      {
        key: "protectedMatters",
        label: "Protected Areas (CAPAD)",
        description:
          "Collaborative Australian Protected Area Database (CAPAD) terrestrial reserves, including national parks and conservation areas.",
        attribution: "DCCEEW — CAPAD Terrestrial (CC BY 4.0)",
        available: true,
      },
      {
        key: "ramsarWetlands",
        label: "Ramsar Wetlands",
        description:
          "Wetlands of international importance listed under the Ramsar Convention, critical for biodiversity and water regulation.",
        attribution:
          "DCCEEW — Directory of Important Wetlands / Ramsar (CC BY 3.0 AU)",
        available: true,
      },
      {
        key: "worldHeritage",
        label: "World Heritage Properties",
        description:
          "Properties inscribed on the UNESCO World Heritage List for outstanding universal cultural or natural value.",
        attribution: "DCCEEW — World Heritage Spatial Database (CC BY 3.0 AU)",
        available: true,
      },
      {
        key: "nationalHeritage",
        label: "National Heritage Places",
        description:
          "Places with outstanding heritage value to the Australian nation, listed under the EPBC Act.",
        attribution:
          "DCCEEW — National Heritage List Spatial Database (CC BY 3.0 AU)",
        available: true,
      },
      {
        key: "commonwealthHeritage",
        label: "Commonwealth Heritage Places",
        description:
          "Commonwealth-owned or leased places with significant heritage value.",
        attribution:
          "DCCEEW — Commonwealth Heritage List Spatial Database (CC BY 3.0 AU)",
        available: true,
      },
      {
        key: "listedCriticalHabitat",
        label: "Listed Critical Habitat",
        description:
          "Habitat critical to the survival of listed threatened species and ecological communities under the EPBC Act.",
        attribution: "DCCEEW — Register of Critical Habitat (CC BY 3.0 AU)",
        available: true,
      },
      {
        key: "regionalForestAgreements",
        label: "Regional Forest Agreements",
        description:
          "Areas covered by Regional Forest Agreements between the Commonwealth and state governments for long-term forest management.",
        attribution:
          "DCCEEW — Regional Forest Agreements Spatial Database (CC BY 3.0 AU)",
        available: true,
      },
      {
        key: "regulatedVeg",
        label: "Regulated Vegetation",
        description:
          "Vegetation regulated under state legislation, including remnant and regrowth vegetation with clearing restrictions.",
        attribution:
          "Queensland Government — Regulated Vegetation Management Map v7.x",
        available: true,
      },
      {
        key: "bushfireHistory",
        label: "Bushfire History",
        description:
          "Historical bushfire and prescribed-burn boundaries (2020–2024) across the South Burnett assessment area.",
        attribution:
          "Geoscience Australia — Bushfire Boundaries Historical (South Burnett, 2020–2024)",
        available: true,
      },
      {
        key: "koalaHabitat",
        label: "Koala Habitat (KHICC)",
        description:
          "Known koala habitat areas identified through the Koala Habitat in a Changing Climate project, critical for koala conservation.",
        attribution:
          "DCCEEW — Known Koala Habitat in a Changing Climate (CC BY 4.0)",
        available: true,
      },
      {
        key: "nvisVegetation",
        label: "NVIS Vegetation",
        description:
          "National vegetation classification rendered using NVIS Level 2 Structural Formation (16 ecological classes — closed/open/sparse forest, woodland, shrubland, grasslands, heath, sedge, aquatic, cleared). Paletted raster derived from the v7.0 vector state files at 50 m.",
        attribution:
          "DCCEEW — National Vegetation Information System v7.0 (CC BY 4.0)",
        available: true,
      },
    ],
  },
  {
    group: "Forest Classification",
    layers: [
      {
        key: "deforestationRisk",
        label: "Forest Loss 2025",
        description:
          "SatSure 10 m forest loss for South Burnett Regional, QLD. Areas with forest loss are red; other areas are transparent. This context layer does not contribute to parcel insights.",
        attribution: "SatSure Inc. — Forest Loss 2025",
        available: true,
        assessmentPeriod: "1 January – 31 December 2025",
      },
      {
        key: "forestChange",
        label: "Forest Change (2020–2025)",
        description:
          "Areas where forest cover has changed between the 2020 baseline and 2025. Tree Loss (brown) and Tree Gain (green).",
        attribution: "SatSure & Geoscape",
        available: true,
        snapshot: "July – December 2020 vs July – December 2025",
      },
      {
        key: "forest2025",
        label: "Forest 2025",
        description:
          "1 m resolution, binary forest / non-forest extent mask for 2025 (no crown-cover breakdown).",
        attribution: "SatSure & Geoscape",
        available: true,
        snapshot: "July – December 2025",
      },
      {
        key: "forest2020",
        label: "Forest 2020 (Baseline)",
        description:
          "1 m resolution, binary forest / non-forest extent mask for 2020, used as the reference for change detection.",
        attribution: "SatSure & Geoscape",
        available: true,
        snapshot: "July – December 2020",
      },
      {
        key: "crownCover2025",
        label: "Crown Cover 2025",
        description:
          "10m resolution, 5-class crown-cover classification superimposed on 1m forest extent",
        attribution: "SatSure & Geoscape",
        available: true,
        snapshot: "July – December 2025",
      },
      {
        key: "crownCover2020",
        label: "Crown Cover 2020 (Baseline)",
        description:
          "10m resolution, 5-class crown-cover classification superimposed on 1m forest extent",
        attribution: "SatSure & Geoscape",
        available: true,
        snapshot: "July – December 2020",
      },
    ],
  },
  {
    group: "Land Use",
    layers: [
      {
        key: "lulc2025",
        label: "Land Use 2025",
        description:
          "10 m resolution, 8-class land use / land cover from SatSure 2025 satellite analysis.",
        attribution: "SatSure & Geoscape",
        available: true,
      },
    ],
  },
  {
    group: "Imagery",
    layers: [
      {
        key: "imageryLatest",
        label: "Latest Imagery",
        description:
          "Cloud-free Sentinel-2 True Colour mosaic, one scene per MGRS tile from the last 45 days as of 24th June 2026.",
        attribution:
          "Copernicus Sentinel-2 — via Microsoft Planetary Computer STAC",
        available: true,
      },
      {
        key: "imagery2025",
        label: "2025 Imagery",
        description:
          "Cloud-free Sentinel-2 True Colour mosaic, one scene per MGRS tile across December 2025.",
        attribution:
          "Copernicus Sentinel-2 — via Microsoft Planetary Computer STAC",
        available: true,
      },
      {
        key: "imagery2024",
        label: "2024 Imagery",
        description:
          "Sentinel-2 True Colour mosaic for South Burnett, using one low-cloud scene per MGRS tile from December 2024.",
        attribution:
          "Copernicus Sentinel-2 — via Microsoft Planetary Computer STAC",
        available: true,
        snapshot: "December 2024",
      },
      {
        key: "imagery2020",
        label: "2020 Imagery",
        description:
          "Cloud-free Sentinel-2 True Colour mosaic, one scene per MGRS tile across Apr–Sep 2020.",
        attribution:
          "Copernicus Sentinel-2 — via Microsoft Planetary Computer STAC",
        available: true,
      },
    ],
  },
];
